import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react'

const DarkModeContext = createContext()

export function useDarkMode() {
  const context = useContext(DarkModeContext)
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider')
  }
  return context
}

export function DarkModeProvider({ children, userId }) {
  const [darkMode, setDarkMode] = useState(false)
  const [loading, setLoading] = useState(true)

  // Load dark mode preference from local storage/system
  useEffect(() => {
    const stored = localStorage.getItem('dark_mode')
    if (stored === 'true' || stored === 'false') {
      setDarkMode(stored === 'true')
    } else {
      const systemPreference = window.matchMedia('(prefers-color-scheme: dark)').matches
      setDarkMode(systemPreference)
    }
    setLoading(false)
  }, [userId])

  // Update document class when dark mode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [darkMode])

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => {
      const newValue = !prev
      localStorage.setItem('dark_mode', newValue ? 'true' : 'false')
      return newValue
    })
  }, [])

  // Memoize context value to prevent unnecessary re-renders of consumers
  const value = useMemo(
    () => ({ darkMode, toggleDarkMode, loading }),
    [darkMode, toggleDarkMode, loading]
  )

  return (
    <DarkModeContext.Provider value={value}>
      {children}
    </DarkModeContext.Provider>
  )
}
